import axiosInstance from './axiosConfig';
import type { Service } from '../features/marketplace/components/ServiceCard';
import type { HomeTopicSectionConfig } from '../features/marketplace/homeTopicSections';
import type { AppointmentTaker } from '../features/marketplace/types';

export type AppContentCountry = 'CA' | 'IN';

export interface PublicHomeItem {
  categoryId: string;
  slug: string;
  topic_key?: string;
  click_action?: 'thread' | 'page';
  page_path?: string;
  title: string;
  shortDesc: string;
  image_url?: string;
  badge?: string;
  badge_type?: 'demand' | 'free' | 'new';
  icon_key?: string;
  meta?: string;
  card_layout?: HomeCardLayout;
}

import type { HomeCardLayout } from '../features/marketplace/constants/homeCardLayouts';

export interface PublicHomeSection {
  id: string;
  eyebrow: string;
  title: string;
  title_lead?: string;
  title_tail?: string;
  subtitle: string;
  card_layout?: HomeCardLayout;
  items: PublicHomeItem[];
}

export type PublicHomeCarouselSlideLayout = 'default' | 'app_stores';

export interface PublicHomeCarouselSlide {
  slug: string;
  kicker: string;
  line1: string;
  line2: string;
  slide_layout: PublicHomeCarouselSlideLayout;
  background_color?: string;
}

export interface PublicStoreProduct {
  id: string;
  slug: string;
  name: string;
  description: string;
  product_type: 'flat_fee' | 'scheduled_seats';
  currency: string;
  price?: number;
  total_amount?: number;
  image_url?: string;
  tags?: string[];
}

function countryParam(country: AppContentCountry): string {
  return country;
}

export async function fetchPublicHomeSections(country: AppContentCountry): Promise<PublicHomeSection[]> {
  const res = await axiosInstance.get<{ sections: PublicHomeSection[] }>('/api/app-content/home', {
    params: { country: countryParam(country), platform: 'web' },
  });
  return res.data.sections ?? [];
}

export async function fetchPublicHomeCarousel(country: AppContentCountry): Promise<PublicHomeCarouselSlide[]> {
  const res = await axiosInstance.get<{ slides: PublicHomeCarouselSlide[] }>('/api/app-content/home-carousel', {
    params: { country: countryParam(country), platform: 'web' },
  });
  return res.data.slides ?? [];
}

export async function fetchPublicStoreProducts(country: AppContentCountry): Promise<PublicStoreProduct[]> {
  const res = await axiosInstance.get<{ products: PublicStoreProduct[] }>('/api/products/public', {
    params: { country: countryParam(country) },
  });
  return res.data.products ?? [];
}

export async function fetchPublicConsultants(country: AppContentCountry): Promise<AppointmentTaker[]> {
  const res = await axiosInstance.get<{ success: boolean; appointmentTakers: AppointmentTaker[] }>(
    '/api/app-content/consultants',
    { params: { country: countryParam(country) } }
  );
  return res.data.appointmentTakers ?? [];
}

const TAG_TO_BADGE: Record<string, { badge: string; badgeType: 'demand' | 'free' | 'new' }> = {
  high_demand: { badge: 'High demand', badgeType: 'demand' },
  free: { badge: 'Free', badgeType: 'free' },
  new: { badge: 'New', badgeType: 'new' },
  course: { badge: 'Course', badgeType: 'new' },
};

export function mapPublicProductToService(product: PublicStoreProduct, image?: string): Service {
  const primaryTag = product.tags?.[0];
  const tagMeta = primaryTag ? TAG_TO_BADGE[primaryTag] : undefined;

  return {
    slug: product.slug,
    title: product.name,
    badge: tagMeta?.badge,
    badgeType: tagMeta?.badgeType,
    category: product.product_type === 'scheduled_seats' ? 'Course' : 'Service',
    price: `${product.currency} $${(product.price ?? product.total_amount ?? 0).toFixed(2)}`,
    image: image ?? product.image_url,
    placeholderColor: '#1a3a5c',
    shortDesc: product.description,
    fullDesc: product.description,
    highlights: ['Backed by CWY quality guarantee', 'Instant access after purchase'],
  };
}

export function mapPublicSectionToHomeConfig(
  section: PublicHomeSection,
  resolveImage: (item: PublicHomeItem) => string | undefined
): HomeTopicSectionConfig {
  return {
    id: section.id,
    cardLayout: section.card_layout ?? 'topic_cards',
    eyebrow: section.eyebrow,
    title: section.title,
    titleLead: section.title_lead,
    titleTail: section.title_tail,
    subtitle: section.subtitle,
    comingSoonKey: section.id === 'professionals' ? 'consultant' : 'service',
    comingSoonEmoji: '✨',
    comingSoonTitle: 'More coming soon!',
    comingSoonSub: 'New services will be added soon.',
    items: section.items.map((item) => ({
      categoryId: item.categoryId,
      slug: item.slug,
      title: item.title,
      shortDesc: item.shortDesc,
      image: resolveImage(item) ?? '',
      badge: item.badge,
      badgeType: item.badge_type,
      iconKey: item.icon_key,
      meta: item.meta,
      cardLayout: item.card_layout,
      topicKey: item.topic_key,
      clickAction: item.click_action,
      pagePath: item.page_path,
    })),
  };
}
